import React, { forwardRef, useState, useRef } from "react";
import { format, isToday, isYesterday } from "date-fns";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Zoom } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/zoom";

import {
  AttachFile,
  Download,
  PlayArrow,
  Close,
  Image,
  VideoCall,
  Description,
  MoreVert,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
import MicIcon from "@mui/icons-material/Mic";
import MessageOptionsModal from "./MessageOptionsModal";
import { notifyError } from "../../../../../utils/ToastifyProvider";
import VoiceMessagePlayer from "../MessageInput/VoiceMessagePlayer";
import CallMessage from "./CallMessage";
import MessageSeenByModal from "./MessageSeenByModal";

// ✅ Helper function to parse URLs and mentions
const parseMessageWithLinks = (text, isMine) => {
  if (!text) return null;

  const URL_REGEX = /(https?:\/\/[^\s]+)/g;

  const urlParts = text.split(URL_REGEX);

  return urlParts.map((part, index) => {
    if (URL_REGEX.test(part)) {
      return (
        <a
          key={`url-${index}`}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className={`underline hover:no-underline break-all ${
            isMine ? "text-blue-200" : "text-blue-600"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {part}
        </a>
      );
    }

    const mentionParts = part.split(/(@[a-zA-Z0-9_]+)/g);
    return mentionParts.map((mentionPart, mentionIndex) => {
      if (mentionPart.startsWith("@")) {
        return (
          <span
            key={`mention-${index}-${mentionIndex}`}
            className={
              isMine
                ? "text-yellow-300 font-semibold cursor-pointer hover:underline"
                : "text-blue-600 font-semibold cursor-pointer hover:underline"
            }
            onClick={(e) => {
              e.stopPropagation();
              console.log("Mention clicked:", mentionPart.substring(1));
            }}
          >
            {mentionPart}
          </span>
        );
      }
      return (
        <React.Fragment key={`text-${index}-${mentionIndex}`}>
          {mentionPart}
        </React.Fragment>
      );
    });
  });
};

const MessagesList = forwardRef(
  (
    {
      msgs,
      userId,
      isTyping,
      typingUsers,
      onScroll,
      onCopy,
      onDelete,
      onReply,
      onEdit,
      chat,
      isFetchingNextPage,
    },
    ref,
  ) => {
    const [fullScreenMedia, setFullScreenMedia] = useState(null);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [showOptionsModal, setShowOptionsModal] = useState(false);
    const [downloadingId, setDownloadingId] = useState(null);
    const [galleryMedia, setGalleryMedia] = useState([]);
    const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
    const [showSeenByModal, setShowSeenByModal] = useState(false);
    const [seenByMessage, setSeenByMessage] = useState(null);
    const videoRefs = useRef({});
    const swiperRef = useRef(null);

    const BASE_DOWNLOAD_URL = process.env.REACT_APP_API_BASE_URL_DOWNLOAD;

    const handleOpenModal = (message) => {
      if (message.messageType === "call") return;

      setSelectedMessage(message);
      setShowOptionsModal(true);
    };

    const handleCloseModal = () => {
      setShowOptionsModal(false);
      setSelectedMessage(null);
    };

    const renderMessageContent = (text, isMine) => {
      return parseMessageWithLinks(text, isMine);
    };

    const formatDateHeader = (dateStr) => {
      const date = new Date(dateStr);
      if (isToday(date)) return "Today";
      if (isYesterday(date)) return "Yesterday";
      return format(date, "MMMM d, yyyy");
    };

    const formatTime = (dateStr) => {
      return new Date(dateStr).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    };

    const getLastUserMessage = () => {
      for (let i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i].sender._id === userId && msgs[i].messageType !== "call") {
          return msgs[i];
        }
      }
      return null;
    };

    const downloadFile = async (fileUrl, fileName, messageId) => {
      try {
        setDownloadingId(messageId);
        const fullUrl = fileUrl.startsWith("http")
          ? fileUrl
          : `${BASE_DOWNLOAD_URL}${fileUrl}`;

        const response = await fetch(fullUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName || fileUrl.split("/").pop() || "download";
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } catch (error) {
        notifyError("Download failed. Please try again.");
      } finally {
        setDownloadingId(null);
      }
    };

    const renderReplyMessage = (replyTo) => {
      if (!replyTo) return null;

      const hasMultipleFiles = replyTo.files && replyTo.files.length > 0;
      const hasSingleFile = replyTo.fileUrl;

      return (
        <div
          className="bg-gray-100 border-l-4 border-[#E74A23] pl-3 pr-2 py-2 mb-2 rounded-r-md cursor-pointer hover:bg-gray-200 transition"
          onClick={() => {
            const target = document.querySelector(
              `[data-message-id="${replyTo._id}"]`,
            );
            if (target) {
              target.scrollIntoView({ behavior: "smooth", block: "center" });
              target.classList.add("highlight-animate");
              setTimeout(() => {
                target.classList.remove("highlight-animate");
              }, 2000);
            }
          }}
        >
          <div className="text-xs font-semibold text-[#E74A23] mb-1">
            {replyTo.sender?.username || "Unknown"}
          </div>
          <div className="text-xs text-gray-600 line-clamp-2">
            {hasMultipleFiles ? (
              <div className="flex items-center gap-1">
                <AttachFile className="w-3 h-3" />
                <span>{replyTo.files.length} files</span>
              </div>
            ) : hasSingleFile ? (
              <div className="flex items-center gap-1">
                {replyTo.fileType === "image" ? (
                  <>
                    <Image className="w-3 h-3" />
                    <span>Photo</span>
                  </>
                ) : replyTo.fileType === "video" ? (
                  <>
                    <VideoCall className="w-3 h-3" />
                    <span>Video</span>
                  </>
                ) : replyTo.fileType === "audio" ? (
                  <>
                    <MicIcon className="w-3 h-3" />
                    <span>Voice message</span>
                  </>
                ) : (
                  <>
                    <Description className="w-3 h-3" />
                    <span>Document</span>
                  </>
                )}
              </div>
            ) : (
              replyTo.content || "Message"
            )}
          </div>
        </div>
      );
    };

    const getCleanFileName = (fileUrl) => {
      const fullName = fileUrl.split("/").pop() || "file";
      const match = fullName.match(/^\w+_\w+_(\d{8})_(.+)$/);
      if (match) {
        return match[2];
      }
      return fullName;
    };

    const openGallery = (files, startIndex, message) => {
      const mediaFiles = files.filter(
        (f) => f.type === "image" || f.type === "video",
      );
      const mediaWithUrls = mediaFiles.map((file) => ({
        ...file,
        url: file.url.startsWith("http")
          ? file.url
          : `${BASE_DOWNLOAD_URL}${file.url}`,
      }));
      setGalleryMedia(mediaWithUrls);
      setCurrentMediaIndex(startIndex);
      setSelectedMessage(message);
      setFullScreenMedia(true);
    };

    const renderSingleFile = (file, isMine, messageId, onClick, message) => {
      const fileUrl = file.url.startsWith("http")
        ? file.url
        : `${BASE_DOWNLOAD_URL}${file.url}`;
      const fileName = file.name || getCleanFileName(file.url);
      const isImage = file.type === "image";
      const isVideo = file.type === "video";
      const isAudio = file.type === "audio";

      const handleClick =
        onClick ||
        (() => {
          if (isImage || isVideo) {
            setGalleryMedia([{ ...file, url: fileUrl }]);
            setCurrentMediaIndex(0);
            setSelectedMessage(message);
            setFullScreenMedia(true);
          }
        });

      if (isAudio) {
        return <VoiceMessagePlayer audioUrl={fileUrl} isMine={isMine} />;
      }

      if (isImage || isVideo) {
        return (
          <div className="relative group w-full">
            {isImage ? (
              <img
                src={fileUrl}
                alt="attachment"
                className="w-full rounded cursor-pointer hover:opacity-90 transition-opacity"
                style={{
                  height: "200px",
                  width: "200px",
                  objectFit: "cover",
                }}
                onClick={handleClick}
              />
            ) : (
              <div
                className="relative cursor-pointer max-w-[300px]"
                onClick={handleClick}
                style={{ height: "200px", width: "170px" }}
              >
                <video
                  ref={(el) => (videoRefs.current[messageId] = el)}
                  className="w-full h-full rounded object-cover pointer-events-none"
                >
                  <source src={fileUrl} type="video/mp4" />
                </video>
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 rounded">
                  <PlayArrow className="w-10 h-10 text-white" />
                </div>
              </div>
            )}

            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  downloadFile(fileUrl, fileName, messageId);
                }}
                className="bg-black bg-opacity-50 text-white rounded-full p-2 hover:bg-opacity-70 transition-all"
              >
                {downloadingId === messageId ? (
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline-block"></span>
                ) : (
                  <Download className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        );
      }

      return (
        <div
          className={`flex items-center gap-2 p-3 rounded max-w-[280px] ${
            isMine ? "bg-blue-600 text-white" : "bg-gray-200 text-black"
          }`}
        >
          <Description fontSize="small" className="flex-shrink-0" />
          <span
            className="text-sm break-words flex-1 min-w-0"
            style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
          >
            {fileName}
          </span>
          <button
            onClick={() => downloadFile(fileUrl, fileName, messageId)}
            className="flex-shrink-0 p-1 rounded-full transition-all hover:bg-white hover:bg-opacity-30"
          >
            {downloadingId === messageId ? (
              <span
                className={`w-5 h-5 border-2 border-t-transparent rounded-full animate-spin inline-block ${
                  isMine ? "border-white" : "border-gray-600"
                }`}
              ></span>
            ) : (
              <Download className="w-5 h-5" />
            )}
          </button>
        </div>
      );
    };

    const renderMultipleFiles = (files, isMine, messageId) => {
      const images = files.filter((f) => f.type === "image");
      const videos = files.filter((f) => f.type === "video");
      const audios = files.filter((f) => f.type === "audio");
      const documents = files.filter((f) => f.type === "document");

      const renderMediaGrid = (mediaFiles, mediaType) => {
        const maxVisible = 4;
        const visibleFiles = mediaFiles.slice(0, maxVisible);
        const remainingCount = mediaFiles.length - maxVisible;

        return (
          <div
            className={`grid max-w-[350px] gap-2 ${
              mediaFiles.length === 1
                ? "grid-cols-1"
                : mediaFiles.length === 2
                  ? "grid-cols-2"
                  : "grid-cols-2"
            }`}
          >
            {visibleFiles.map((file, index) => {
              const isLast = index === visibleFiles.length - 1;
              const showOverlay = isLast && remainingCount > 0;

              return (
                <div key={index} className="relative group">
                  {renderSingleFile(
                    file,
                    isMine,
                    `${messageId}-${mediaType}-${index}`,
                    () => openGallery(mediaFiles, index),
                  )}
                  {showOverlay && (
                    <div
                      className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center rounded cursor-pointer z-10"
                      onClick={(e) => {
                        e.stopPropagation();
                        openGallery(mediaFiles, maxVisible);
                      }}
                    >
                      <span className="text-white text-3xl font-normal">
                        +{remainingCount}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      };

      return (
        <div className="space-y-2">
          {images.length > 0 && renderMediaGrid(images, "img")}
          {videos.length > 0 && renderMediaGrid(videos, "vid")}
          {audios.length > 0 && (
            <div className="space-y-2">
              {audios.map((file, index) => (
                <div key={index}>
                  {renderSingleFile(file, isMine, `${messageId}-aud-${index}`)}
                </div>
              ))}
            </div>
          )}
          {documents.length > 0 && (
            <div className="space-y-2">
              {documents.map((file, index) => (
                <div key={index}>
                  {renderSingleFile(file, isMine, `${messageId}-doc-${index}`)}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    };

    const renderFiles = (message, isMine) => {
      if (message.files && message.files.length > 0) {
        if (message.files.length === 1) {
          const file = message.files[0];
          return renderSingleFile(file, isMine, message._id, () => {
            if (file.type === "image" || file.type === "video") {
              openGallery(message.files, 0);
            }
          });
        }
        return renderMultipleFiles(message.files, isMine, message._id);
      }

      if (message.fileUrl) {
        const file = {
          url: message.fileUrl,
          type: message.fileType,
          name: getCleanFileName(message.fileUrl),
        };
        return renderSingleFile(file, isMine, message._id, () => {
          if (file.type === "image" || file.type === "video") {
            openGallery([file], 0);
          }
        });
      }

      return null;
    };

    const renderMessageStatus = (message, isLastUserMessage) => {
      if (message.sender._id !== userId) return null;
      if (!isLastUserMessage) return null;

      const readByUsers = (message.readBy || []).filter((u) => {
        const readUserId = typeof u === "string" ? u : u._id;
        return readUserId !== userId;
      });
      const isGroupChat = chat?.users?.length > 2;

      if (readByUsers.length > 0) {
        if (isGroupChat) {
          const seenCount = readByUsers.length;
          return (
            <div className="flex items-center justify-end gap-1 mt-1 text-right">
              <div className="flex flex-col items-end text-[10px] text-gray-400">
                <div
                  className="flex items-center gap-1 cursor-pointer hover:text-blue-600 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSeenByMessage(message);
                    setShowSeenByModal(true);
                  }}
                >
                  <span className="text-blue-500 font-bold">✓✓</span>
                  <span className="hover:underline">Seen by {seenCount}</span>
                </div>
              </div>
            </div>
          );
        } else {
          return (
            <div className="flex items-center justify-end gap-1 mt-1 text-right text-[10px] text-gray-400">
              <span className="text-blue-500 font-bold">✓✓</span>
              <span>Seen</span>
            </div>
          );
        }
      }

      if (message.delivered || message.deliveredTo?.length > 0) {
        return (
          <div className="flex items-center justify-end gap-1 mt-1 text-right text-[10px] text-gray-400">
            <span className="text-gray-400 font-bold">✓✓</span>
            <span>Delivered</span>
          </div>
        );
      }

      return (
        <div className="flex items-center justify-end gap-1 mt-1 text-right text-[10px] text-gray-400">
          <span className="font-bold">✓</span>
          <span>Sent</span>
        </div>
      );
    };

    const getTypingText = () => {
      const typingArray = Array.from(typingUsers);
      if (typingArray.length === 0) return null;
      if (typingArray.length === 1) {
        return `${typingArray[0].username || "Someone"} is typing...`;
      } else {
        return `typing...`;
      }
    };

    const lastUserMessage = getLastUserMessage();

    return (
      <>
        <div
          ref={ref}
          onScroll={onScroll}
          className="flex-1 overflow-y-auto p-4 space-y-3 relative bg-[#FFFFFF]"
        >
          {isFetchingNextPage && (
            <div className="flex justify-center items-center p-4">
              <div className="w-6 h-6 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
            </div>
          )}
          {msgs.length > 0 ? (
            msgs.map((m, index) => {
              const isMine = m.sender._id === userId;
              const showDateHeader =
                index === 0 ||
                new Date(msgs[index - 1].createdAt).toDateString() !==
                  new Date(m.createdAt).toDateString();
              const isLastUserMessage =
                lastUserMessage && lastUserMessage._id === m._id;

              const hasFiles = (m.files && m.files.length > 0) || m.fileUrl;

              if (m.messageType === "call") {
                return (
                  <div key={m._id}>
                    {showDateHeader && (
                      <div className="flex justify-center mb-4">
                        <div className="bg-gray-100 text-gray-600 text-xs px-3 py-1 rounded-full">
                          {formatDateHeader(m.createdAt)}
                        </div>
                      </div>
                    )}
                    <CallMessage message={m} currentUserId={userId} />
                  </div>
                );
              }

              return (
                <div key={m._id}>
                  {showDateHeader && (
                    <div className="flex justify-center mb-4">
                      <div className="bg-gray-100 text-gray-600 text-xs px-3 py-1 rounded-full">
                        {formatDateHeader(m.createdAt)}
                      </div>
                    </div>
                  )}

                  <div
                    className={`flex ${
                      isMine ? "justify-end" : "justify-start"
                    } group`}
                    data-message-id={m._id}
                    data-sender-id={m.sender._id}
                  >
                    <div className="flex flex-col max-w-[75%] relative">
                      <div
                        className={`px-4 py-2 rounded-2xl text-sm shadow-sm relative break-words ${
                          isMine
                            ? "bg-[#0070f0] text-white rounded-br-none"
                            : "bg-[#F3F4f6] text-black rounded-bl-none"
                        }`}
                        style={{
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {chat?.users?.length > 2 && !isMine && (
                          <div className="text-xs font-semibold text-[#E74A23] mb-1">
                            {m.sender?.username || "Unknown"}
                          </div>
                        )}

                        {m.replyTo && renderReplyMessage(m.replyTo)}

                        {hasFiles && (
                          <div className="mb-2">{renderFiles(m, isMine)}</div>
                        )}

                        {m.content && (
                          <div className="whitespace-pre-wrap leading-relaxed break-words">
                            {renderMessageContent(m.content, isMine)}
                          </div>
                        )}

                        <div
                          className={`flex items-center justify-between text-[10px] mt-1 gap-2 ${
                            isMine ? "text-gray-200" : "text-gray-600"
                          }`}
                        >
                          <span>{formatTime(m.createdAt)}</span>
                          {/* ✅ NEW: Show "edited" indicator */}
                          {m.edited && (
                            <span className="italic opacity-75">edited</span>
                          )}
                        </div>
                      </div>

                      <button
                        onClick={() => handleOpenModal(m)}
                        className="absolute top-1 right-0 text-gray-400 hover:text-black opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <MoreVert fontSize="small" />
                      </button>

                      {isMine && isLastUserMessage && (
                        <div className="group">
                          {renderMessageStatus(m, true)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
              <div className="text-center">
                <div className="text-2xl mb-2">💬</div>
                <div>No messages yet</div>
                <div className="text-xs mt-1">Start the conversation!</div>
              </div>
            </div>
          )}

          {isTyping && (
            <div className="flex justify-start">
              <div className="bg-gray-200 px-4 py-2 rounded-2xl rounded-bl-none">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"></div>
                    <div
                      className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"
                      style={{ animationDelay: "0.1s" }}
                    ></div>
                    <div
                      className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"
                      style={{ animationDelay: "0.2s" }}
                    ></div>
                  </div>
                  <span className="text-xs text-gray-600">
                    {getTypingText()}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {fullScreenMedia && galleryMedia.length > 0 && (
          <div className="fixed inset-0 bg-black z-50 flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 bg-[#202C33]">
              <div className="flex items-center gap-3 flex-1"></div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const currentMedia = galleryMedia[currentMediaIndex];
                    downloadFile(
                      currentMedia.url,
                      currentMedia.name || getCleanFileName(currentMedia.url),
                    );
                  }}
                  className="text-white hover:bg-white hover:bg-opacity-10 p-2 rounded-full transition-all"
                  title="Download"
                >
                  <Download className="w-5 h-5" />
                </button>
                <button
                  onClick={() => {
                    setFullScreenMedia(null);
                    setGalleryMedia([]);
                  }}
                  className="text-white hover:bg-white hover:bg-opacity-10 p-2 rounded-full transition-all"
                  title="Close"
                >
                  <Close className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 flex items-center justify-center bg-black relative overflow-hidden">
              {currentMediaIndex > 0 && (
                <button
                  onClick={() => swiperRef.current?.swiper.slidePrev()}
                  className="absolute left-4 z-10 bg-white bg-opacity-70 hover:bg-opacity-90 text-black p-3 rounded-full transition-all"
                >
                  <ChevronLeft className="w-8 h-8" />
                </button>
              )}
              {currentMediaIndex < galleryMedia.length - 1 && (
                <button
                  onClick={() => swiperRef.current?.swiper.slideNext()}
                  className="absolute right-4 z-10 bg-white bg-opacity-70 hover:bg-opacity-90 text-black p-3 rounded-full transition-all"
                >
                  <ChevronRight className="w-8 h-8" />
                </button>
              )}

              <Swiper
                ref={swiperRef}
                modules={[Navigation, Pagination, Zoom]}
                initialSlide={currentMediaIndex}
                onSlideChange={(swiper) =>
                  setCurrentMediaIndex(swiper.activeIndex)
                }
                zoom={true}
                spaceBetween={0}
                className="w-full h-full select-none"
              >
                {galleryMedia.map((media, index) => (
                  <SwiperSlide key={index}>
                    <div className="swiper-zoom-container flex items-center justify-center w-full h-full bg-black">
                      {media.type === "image" ? (
                        <img
                          src={media.url}
                          alt={`Media ${index + 1}`}
                          className="object-contain m-auto rounded"
                          style={{
                            width: "auto",
                            height: "auto",
                            maxHeight: "90vh",
                            maxWidth: "100vw",
                          }}
                        />
                      ) : (
                        <video
                          src={media.url}
                          controls
                          autoPlay
                          className="object-contain m-auto rounded"
                          style={{
                            width: "auto",
                            height: "auto",
                            maxHeight: "90vh",
                            maxWidth: "100vw",
                          }}
                        />
                      )}
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>

              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-gray-800 bg-opacity-80 px-4 py-2 rounded-full">
                <span className="text-white text-sm font-medium">
                  {currentMediaIndex + 1} of {galleryMedia.length}
                </span>
              </div>
            </div>
          </div>
        )}

        {showOptionsModal && selectedMessage && (
          <MessageOptionsModal
            isOpen={showOptionsModal}
            onClose={handleCloseModal}
            message={selectedMessage}
            isMine={selectedMessage?.sender._id === userId}
            onCopy={onCopy}
            onReply={onReply}
            onDelete={onDelete}
            onDownload={downloadFile}
            onEdit={onEdit}
          />
        )}

        {showSeenByModal && seenByMessage && (
          <MessageSeenByModal
            isOpen={showSeenByModal}
            onClose={() => {
              setShowSeenByModal(false);
              setSeenByMessage(null);
            }}
            message={seenByMessage}
            chat={chat}
            currentUserId={userId}
          />
        )}
      </>
    );
  },
);

MessagesList.displayName = "MessagesList";

export default MessagesList;
